import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { track } from "../lib/analytics";
import { STATES, PROJECT_TYPES, CONNECTIVITY_OPTIONS, VOLTAGE_OPTIONS, SITE_FLAGS, BUSINESS_MODELS } from "../lib/constants";
import { Sparkles, Pencil, ArrowRight, ChevronDown, MessageSquareText, ListChecks } from "lucide-react";
import { toast } from "sonner";

const FIELD_DEFS = {
  project_type: { label: "Project type", options: PROJECT_TYPES.map((p) => ({ value: p.value, label: p.label })) },
  bess_included: { label: "Battery storage included", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
  capacity_mw: { label: "Capacity", type: "number", suffix: (a) => (a.project_type === "bess" ? "MWh" : "MW") },
  state: { label: "State", options: STATES.map((s) => ({ value: s, label: s })) },
  district: { label: "District", type: "text" },
  connectivity: { label: "Grid connectivity", options: CONNECTIVITY_OPTIONS.filter((c) => c.value !== "not_sure").map((c) => ({ value: c.value, label: c.label })) },
  voltage_kv: { label: "Voltage level", options: VOLTAGE_OPTIONS.map((v) => ({ value: v, label: v === "not_sure" ? "Not sure" : `${v} kV` })) },
  dedicated_line: { label: "Dedicated transmission line", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }, { value: "not_sure", label: "Not sure" }] },
  line_length_km: { label: "Line length (km)", type: "number" },
  land_type: { label: "Land type", options: [{ value: "private", label: "Private" }, { value: "government", label: "Government revenue" }, { value: "mixed", label: "Mixed" }, { value: "not_sure", label: "Not sure" }] },
  business_model: { label: "Power use / sale", options: BUSINESS_MODELS.map((b) => ({ value: b.value, label: b.label })) },
  crossed_tl_voltage: { label: "Crossed line voltage (kV)", type: "text" },
  ...Object.fromEntries(SITE_FLAGS.map((f) => [f.field, {
    label: f.label, flag: true,
    options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }, { value: "unsure", label: "Unsure" }],
  }])),
};

const ORDER = ["project_type", "bess_included", "capacity_mw", "state", "district", "connectivity", "voltage_kv", "dedicated_line", "line_length_km", "land_type", "business_model"];
const REQUIRED = ["project_type", "state", "connectivity", "voltage_kv", "dedicated_line", "land_type", "business_model"];
const FLAG_FIELDS = SITE_FLAGS.map((f) => f.field);

const valueLabel = (field, answers) => {
  const def = FIELD_DEFS[field];
  const v = answers[field];
  if (def.options) return def.options.find((o) => o.value === v)?.label || v;
  return def.suffix ? `${v} ${def.suffix(answers)}` : String(v);
};

function Editor({ field, answers, set, autoFocus }) {
  const def = FIELD_DEFS[field];
  const cls = "mt-1 w-full bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none";
  if (def.options) return (
    <select value={answers[field] || ""} onChange={(e) => set({ [field]: e.target.value })} className={cls} autoFocus={autoFocus} data-testid={`describe-edit-${field}`}>
      <option value="">Select…</option>
      {def.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
  return <input type={def.type === "number" ? "number" : "text"} min="0" value={answers[field] || ""}
    onChange={(e) => set({ [field]: e.target.value })} className={cls} autoFocus={autoFocus} data-testid={`describe-edit-${field}`} />;
}

function UnderstoodRow({ field, conf, answers, set }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="bg-white border border-[#0B1F3A]/15 rounded-lg px-4 py-2.5" data-testid={`understood-field-${field}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-[#475569] font-semibold">{FIELD_DEFS[field].label}</p>
          {!editing && <p className="text-sm font-medium text-[#0B1F3A] truncate">{valueLabel(field, answers)}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {conf === "low" && !editing && (
            <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`guess-badge-${field}`}>my best guess — confirm</span>
          )}
          <button onClick={() => setEditing(!editing)} className="text-[#1D4ED8] hover:text-[#1638A8]" data-testid={`edit-btn-${field}`} aria-label={`Edit ${FIELD_DEFS[field].label}`}>
            <Pencil size={13} />
          </button>
        </div>
      </div>
      {editing && <Editor field={field} answers={answers} set={set} autoFocus />}
    </div>
  );
}

function GreenProcurementCard() {
  return (
    <div className="mt-5 bg-white border-2 border-[#059669] rounded-lg px-5 py-4" data-testid="describe-green-procurement">
      <p className="font-serif font-semibold text-[#0B1F3A]">Buying green power? Use the Switch Analyzer instead</p>
      <p className="text-sm text-[#475569] mt-1">It compares your 4 green-power routes with indicative savings — built exactly for this case.</p>
      <Link to="/analyzer" className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white rounded-md px-4 py-2 text-sm font-medium mt-3">Open the Analyzer <ArrowRight size={14} /></Link>
    </div>
  );
}

function MissingFields({ missing, answers, set }) {
  return (
    <div className="mt-6" data-testid="describe-missing">
      <h2 className="font-serif text-lg text-[#0B1F3A]">A few things I still need</h2>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {missing.map((f) => (
          <div key={f} className="bg-white border-2 border-[#1D4ED8]/30 rounded-lg px-4 py-3" data-testid={`missing-field-${f}`}>
            <p className="text-[11px] uppercase tracking-wider text-[#1D4ED8] font-semibold">{FIELD_DEFS[f].label}</p>
            <Editor field={f} answers={answers} set={set} />
          </div>
        ))}
        {!answers.capacity_mw && (
          <div className="bg-white border border-[#0B1F3A]/15 rounded-lg px-4 py-3" data-testid="missing-field-capacity_mw">
            <p className="text-[11px] uppercase tracking-wider text-[#475569] font-semibold">Capacity ({answers.project_type === "bess" ? "MWh" : "MW"}) — optional</p>
            <Editor field="capacity_mw" answers={answers} set={set} />
          </div>
        )}
      </div>
    </div>
  );
}

function FlagsSection({ answers, set, meta, flagsOpen, setFlagsOpen, flagsExtracted }) {
  return (
    <div className="mt-6 bg-white border border-[#0B1F3A]/15 rounded-lg" data-testid="describe-flags-section">
      <button onClick={() => setFlagsOpen(!flagsOpen)} className="w-full flex items-center justify-between px-4 py-3" data-testid="describe-flags-toggle">
        <p className="text-sm font-medium text-[#0B1F3A]">Site & route conditions {flagsExtracted.length > 0 ? `· ${flagsExtracted.length} spotted in your description` : "· all set to 'No' — double-check"}</p>
        <ChevronDown size={16} className={`text-[#475569] transition-transform ${flagsOpen ? "rotate-180" : ""}`} />
      </button>
      {flagsOpen && (
        <div className="px-4 pb-4 space-y-2">
          {FLAG_FIELDS.map((f) => (
            <div key={f} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 border-t border-[#0B1F3A]/10 pt-2" data-testid={`describe-flag-${f}`}>
              <p className="text-xs text-[#0B1F3A]">{FIELD_DEFS[f].label}{meta[f]?.confidence === "low" && <span className="ml-2 text-[10px] font-semibold text-amber-700">best guess</span>}</p>
              <div className="flex gap-1.5">
                {FIELD_DEFS[f].options.map((o) => (
                  <button key={o.value} type="button" onClick={() => set({ [f]: o.value })}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${answers[f] === o.value ? "bg-[#0B1F3A] text-[#F5F8FF] border-[#0B1F3A]" : "bg-white border-[#0B1F3A]/20 text-[#475569] hover:border-[#059669]"}`}
                    data-testid={`describe-flag-${f}-${o.value}`}>{o.label}</button>
                ))}
              </div>
            </div>
          ))}
          {answers.crosses_tl === "yes" && (
            <div className="border-t border-[#0B1F3A]/10 pt-2">
              <p className="text-xs text-[#0B1F3A]">Crossed line voltage (kV, if known)</p>
              <Editor field="crossed_tl_voltage" answers={answers} set={set} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DescribeReview({ answers, set, meta, summary, flagsOpen, setFlagsOpen, onGenerate, onSwitchMode, onRewrite }) {
  const understood = ORDER.filter((f) => meta[f] && answers[f]);
  const flagsExtracted = FLAG_FIELDS.filter((f) => meta[f]);
  const missing = REQUIRED.filter((f) => !answers[f]);
  const isGreenProcurement = answers.project_type === "green_procurement";
  const ready = missing.length === 0 && !isGreenProcurement;

  return (
    <div className="max-w-2xl fade-up" data-testid="describe-review">
      <h1 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">Here's what I understood</h1>
      {summary && <p className="text-sm text-[#475569] mt-2 italic" data-testid="describe-summary">"{summary}"</p>}
      <div className="space-y-2 mt-5">
        {understood.map((f) => <UnderstoodRow key={f} field={f} conf={meta[f].confidence} answers={answers} set={set} />)}
      </div>

      {isGreenProcurement && <GreenProcurementCard />}
      {!isGreenProcurement && missing.length > 0 && <MissingFields missing={missing} answers={answers} set={set} />}
      {!isGreenProcurement && (
        <FlagsSection answers={answers} set={set} meta={meta} flagsOpen={flagsOpen} setFlagsOpen={setFlagsOpen} flagsExtracted={flagsExtracted} />
      )}

      <div className="flex flex-wrap items-center gap-4 mt-8">
        {!isGreenProcurement && (
          <button onClick={onGenerate} disabled={!ready}
            className="inline-flex items-center gap-2 bg-[#059669] text-white rounded-md px-6 py-3 text-sm font-bold hover:bg-[#059669]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-transform hover:-translate-y-0.5"
            data-testid="describe-generate-btn">
            Build my roadmap <ArrowRight size={15} />
          </button>
        )}
        <button onClick={onRewrite} className="text-xs text-[#475569] underline" data-testid="describe-rewrite-btn">Rewrite my description</button>
        <button onClick={onSwitchMode} className="inline-flex items-center gap-1.5 text-xs text-[#1D4ED8] underline" data-testid="describe-review-switch-steps">
          <ListChecks size={13} /> Review in step-by-step instead
        </button>
      </div>
      {!ready && !isGreenProcurement && missing.length > 0 && (
        <p className="text-xs text-[#475569] mt-2">Fill the {missing.length} highlighted field{missing.length > 1 ? "s" : ""} above to build your roadmap.</p>
      )}
      <p className="text-[11px] text-[#475569] mt-4 flex items-center gap-1.5"><MessageSquareText size={12} className="text-[#059669]" /> AI only fills this form — your roadmap itself comes from Solarian's expert-verified rules engine, never from AI.</p>
    </div>
  );
}

export default function DescribeProject({ answers, set, onGenerate, onSwitchMode }) {
  const [stage, setStage] = useState("input");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({});
  const [summary, setSummary] = useState("");
  const [flagsOpen, setFlagsOpen] = useState(false);

  const analyze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wizard/extract", { text });
      const patch = {};
      Object.entries(data.fields).forEach(([f, m]) => { patch[f] = typeof m.value === "number" ? String(m.value) : m.value; });
      set(patch);
      setMeta(data.fields);
      setSummary(data.summary);
      setFlagsOpen(FLAG_FIELDS.some((f) => data.fields[f]));
      setStage("review");
      track("wizard_describe_extracted", { fields: Object.keys(data.fields).length });
    } catch (e) {
      toast.error(apiError(e, "AI extraction is unavailable — switch to step-by-step instead"));
    } finally {
      setBusy(false);
    }
  };

  if (stage === "input") return (
    <div className="max-w-2xl fade-up" data-testid="describe-input-stage">
      <h1 className="font-serif text-2xl sm:text-3xl text-[#0B1F3A]">Describe your project in your own words</h1>
      <p className="text-sm text-[#475569] mt-2">Mention whatever you know — capacity, state, land, grid connection, who uses the power, site conditions. Our AI fills the form and asks only what's missing.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6}
        placeholder="e.g., We're building a 50 MW solar plant on private land in Rajasthan, connecting to the state grid at 132 kV with a 12 km dedicated line. Power will be sold to a third party. The route crosses a national highway."
        className="mt-5 w-full bg-white border border-[#0B1F3A]/20 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none"
        data-testid="describe-textarea" />
      <div className="flex flex-wrap items-center gap-4 mt-4">
        <button onClick={analyze} disabled={busy || text.trim().length < 15}
          className="inline-flex items-center gap-2 bg-[#059669] text-white rounded-md px-6 py-3 text-sm font-bold hover:bg-[#059669]/90 disabled:opacity-40 transition-transform hover:-translate-y-0.5"
          data-testid="describe-analyze-btn">
          <Sparkles size={15} /> {busy ? "Reading your description…" : "Analyze my description"}
        </button>
        <button onClick={onSwitchMode} className="inline-flex items-center gap-1.5 text-xs text-[#1D4ED8] underline" data-testid="describe-switch-steps">
          <ListChecks size={13} /> Prefer clicking? Switch to step-by-step
        </button>
      </div>
    </div>
  );

  return (
    <DescribeReview answers={answers} set={set} meta={meta} summary={summary}
      flagsOpen={flagsOpen} setFlagsOpen={setFlagsOpen}
      onGenerate={onGenerate} onSwitchMode={onSwitchMode} onRewrite={() => setStage("input")} />
  );
}
